import { SetStateAction, Dispatch, useEffect, useState, useMemo } from "react";
import { Body1 } from "../Typography";
import {
  ParsedBlock,
  renderDenialLanguage,
  renderAsMarkup,
  SimulatedDenialLetterInstructiveText,
} from "./DenialLetterUtil";
// eslint-disable-next-line cohere-react/no-mui-styled-import
import { styled } from "@material-ui/core";
import { colorsLight } from "../../themes";
import { MdReview, NurseReview, PeerToPeerReview, ServiceRequestResponse } from "@coherehealth/core-platform-api";
import Mustache from "mustache";
import { AutoFillIcon, useConfiguration, useFeature } from "../../components";
import { DenialLetterTextBoxWithGradeLevel } from "./DenialLetterTextBoxWithGradeLevel";
import { DecisionExplanation } from "./DecisionExplanation";

interface Props {
  parsedMarkupText?: ParsedBlock[];
  denialLanguage?: string;
  setDenialLanguage?: Dispatch<SetStateAction<string>>;
  setEditedMarkupText?: Dispatch<SetStateAction<string>>;
  isBlob?: boolean;
  serviceRequest?: ServiceRequestResponse | null;
  review?: MdReview | PeerToPeerReview | NurseReview | null;
  isReadOnly?: boolean;
  setCanSubmitForReview?: Dispatch<SetStateAction<boolean>>;
  mdNote?: string;
  setMdNote?: Dispatch<SetStateAction<string>>;
  guidelinesDenied?: string;
  setGuidelinesDenied?: Dispatch<SetStateAction<string>>;
  mdDecisionExplanation?: string;
  setMdDecisionExplanation?: Dispatch<SetStateAction<string>>;
}

export default function DenialLetterBody({
  parsedMarkupText,
  denialLanguage,
  setDenialLanguage,
  setEditedMarkupText,
  isBlob,
  serviceRequest,
  review,
  isReadOnly,
  setCanSubmitForReview,
  mdNote,
  setMdNote,
  guidelinesDenied,
  setGuidelinesDenied,
  mdDecisionExplanation,
  setMdDecisionExplanation,
}: Props) {
  const [letterState, setLetterState] = useState<{ [key: number]: string }>({});
  const translatedMdNoteEnabled = useFeature("translatedMdNote");
  const logrocketUnmasking = useFeature("logrocketUnmasking");

  const denialReadingLevelConfig = useConfiguration(
    "reviewConfiguration",
    serviceRequest?.healthPlanName,
    serviceRequest?.delegatedVendor
  );

  const hasTranslatedMdNote =
    (review?.reviewType === "MdReview" || review?.reviewType === "PeerToPeerReview") &&
    (review.translatedMdNote || review.overwrittenTranslatedMdNote);

  const denialReadingLevelConfigEnabled = Boolean(denialReadingLevelConfig?.denialReadingLevel?.enabled);
  const approvedPxCodes = review?.approvedSemanticProcedureCodes?.map((item) => item?.code).join(", ");
  const computedDecisionExplanation =
    review?.reviewType === "MdReview" || review?.reviewType === "PeerToPeerReview"
      ? review.computedDecisionExplanation
      : "";
  const deniedPxCodes = serviceRequest?.semanticProcedureCodes
    ?.filter((item) => {
      if (review?.approvedSemanticProcedureCodes?.find((element) => item.code === element.code)) {
        return "";
      } else {
        return item.code;
      }
    })
    .map((item) => item?.code)
    .join(", ");

  const templateMapping: { [key: string]: string | number | undefined } = useMemo(() => {
    return {
      approvedQuantity: review?.approvedUnits || "Approved Units",
      unitType: serviceRequest?.unitType?.toLocaleLowerCase().replaceAll("_", " ") || "Number of Service Dates",
      approvedProcedureCodes: approvedPxCodes || "Approved Procedure Codes",
      deniedQuantity:
        review?.approvedUnits && serviceRequest?.units
          ? serviceRequest?.units - review?.approvedUnits
          : "Denied Quantity",
      deniedProcedureCodes: deniedPxCodes,
      startDate: serviceRequest?.startDate || "Start Date",
      endDate: serviceRequest?.endDate || "End Date",
      units: serviceRequest?.units,
      translatedMdNote: mdNote,
      guidelinesDenied,
      mdDecisionExplanation,
    };
  }, [
    review?.approvedUnits,
    serviceRequest?.unitType,
    serviceRequest?.units,
    serviceRequest?.startDate,
    serviceRequest?.endDate,
    approvedPxCodes,
    deniedPxCodes,
    mdNote,
    guidelinesDenied,
    mdDecisionExplanation,
  ]);

  useEffect(() => {
    let compiled: { [key: number]: string } = {};
    parsedMarkupText?.forEach((item, index) => {
      compiled[index] = item.text || "";
    });
    setLetterState(compiled);
  }, [parsedMarkupText]);

  useEffect(() => {
    if (!isBlob) {
      const compiledEditedDenialText = renderDenialLanguage({
        parsedMarkupText: parsedMarkupText || [],
        templateMapping,
        translatedMdNoteEnabled,
        letterState,
      });
      setDenialLanguage?.(compiledEditedDenialText);
    }
    denialLanguage?.length ? setCanSubmitForReview?.(true) : setCanSubmitForReview?.(false);
  }, [
    denialLanguage?.length,
    isBlob,
    letterState,
    parsedMarkupText,
    setCanSubmitForReview,
    setDenialLanguage,
    templateMapping,
    translatedMdNoteEnabled,
  ]);

  useEffect(() => {
    if (parsedMarkupText) {
      setEditedMarkupText?.(renderAsMarkup(parsedMarkupText, letterState));
    }
  }, [letterState, parsedMarkupText, setEditedMarkupText]);
  return (
    <div style={{ whiteSpace: "pre-wrap", width: "100%", marginBottom: 50 }}>
      {isReadOnly ? (
        <ReadOnlyLetterText>{denialLanguage}</ReadOnlyLetterText>
      ) : isBlob ? (
        <DenialLetterTextBoxWithGradeLevel
          fullWidth
          multiline
          minRows={"3"}
          value={denialLanguage || ""}
          onChangeValue={(value) => {
            setDenialLanguage?.(value);
          }}
          denialReadingLevelConfigEnabled={denialReadingLevelConfigEnabled}
        />
      ) : (
        parsedMarkupText?.map((item, index) => {
          if (item.type === "STATIC") {
            return <ReadOnlyLetterText key={index}>{item.text}</ReadOnlyLetterText>;
          } else if (item.type === "INSTRUCTIVE") {
            return (
              <SimulatedDenialLetterInstructiveText data-public={!!logrocketUnmasking ? true : undefined} key={index}>
                {item.text}
              </SimulatedDenialLetterInstructiveText>
            );
          } else if (item.type === "INJECTED") {
            return <InjectedText key={index}>{Mustache.render("{{" + item.text + "}}", templateMapping)}</InjectedText>;
          } else if (item.type === "MDNOTE") {
            return (
              translatedMdNoteEnabled && (
                <DenialLetterTextBoxWithGradeLevel
                  fullWidth
                  multiline
                  key={index}
                  minRows={"3"}
                  value={mdNote}
                  onChangeValue={(value) => {
                    setMdNote?.(value);
                    setLetterState((prev) => ({ ...prev, [index]: value }));
                  }}
                  denialReadingLevelConfigEnabled={denialReadingLevelConfigEnabled}
                  InputProps={{
                    endAdornment: hasTranslatedMdNote ? (
                      <div style={{ marginTop: "-15px" }}>
                        <AutoFillIcon title="This text was automatically generated from the MD review" />
                      </div>
                    ) : undefined,
                  }}
                />
              )
            );
          } else if (item.type === "GUIDELINES_DENIED") {
            return (
              <DenialLetterTextBoxWithGradeLevel
                fullWidth
                multiline
                key={index}
                minRows={"3"}
                value={guidelinesDenied}
                onChangeValue={(value) => {
                  setGuidelinesDenied?.(value);
                  setLetterState((prev) => ({ ...prev, [index]: value }));
                }}
                denialReadingLevelConfigEnabled={denialReadingLevelConfigEnabled}
              />
            );
          } else if (item.type === "MD_DECISION_EXPALANATION") {
            return hasTranslatedMdNote ? (
              <div key={index}>
                <ReadOnlyLetterText>Decision explanation</ReadOnlyLetterText>
                <DenialLetterTextBoxWithGradeLevel
                  fullWidth
                  multiline
                  minRows={"3"}
                  value={mdDecisionExplanation}
                  onChangeValue={(value) => {
                    setMdDecisionExplanation?.(value);
                    setLetterState((prev) => ({ ...prev, [index]: value }));
                  }}
                  denialReadingLevelConfigEnabled={denialReadingLevelConfigEnabled}
                  InputProps={{
                    endAdornment: (
                      <div style={{ marginTop: "-15px" }}>
                        <AutoFillIcon title="This text was automatically generated from the MD review" />
                      </div>
                    ),
                  }}
                />
              </div>
            ) : computedDecisionExplanation ? (
              <div key={index}>
                <ReadOnlyLetterText>Decision explanation</ReadOnlyLetterText>

                <DecisionExplanation
                  mdDecisionExplanation={mdDecisionExplanation}
                  setMdDecisionExplanation={(value) => {
                    setMdDecisionExplanation?.(value);
                  }}
                  denialReadingLevelConfigEnabled={denialReadingLevelConfigEnabled}
                  index={index}
                  setLetterState={(value) => {
                    setLetterState((prev) => ({ ...prev, [index]: value }));
                  }}
                />
              </div>
            ) : (
              <div key={index}>
                <ReadOnlyLetterText>Decision explanation</ReadOnlyLetterText>
                <DenialLetterTextBoxWithGradeLevel
                  fullWidth
                  multiline
                  minRows={"3"}
                  value={mdDecisionExplanation}
                  onChangeValue={(value) => {
                    setMdDecisionExplanation?.(value);
                    setLetterState((prev) => ({ ...prev, [index]: value }));
                  }}
                  denialReadingLevelConfigEnabled={denialReadingLevelConfigEnabled}
                />
              </div>
            );
          } else {
            return (
              <DenialLetterTextBoxWithGradeLevel
                fullWidth
                multiline
                key={index}
                minRows={"3"}
                value={letterState[index] || ""}
                onChangeValue={(value) => {
                  setLetterState({ ...letterState, [index]: value });
                }}
                denialReadingLevelConfigEnabled={denialReadingLevelConfigEnabled}
              />
            );
          }
        })
      )}
    </div>
  );
}

// eslint-disable-next-line cohere-react/no-mui-styled-import
const ReadOnlyLetterText = styled(Body1)({
  display: "inline",
  color: colorsLight.font.main,
});

// eslint-disable-next-line cohere-react/no-mui-styled-import
const InjectedText = styled(Body1)({
  display: "inline",
  color: colorsLight.font.main,
});
